import axios from 'axios';

export interface ServerDiagnostics {
  diagnostics: {
    total: number;
    byType: Record<string, number>;
    memory: {
      rss: number;
      heapTotal: number;
      heapUsed: number;
      external: number;
      arrayBuffers?: number;
    };
    uptime: number;
    webSocketStats?: {
      totalConnections: number;
    };
    webSocketDetails?: WebSocketDetail[];
    wsConnections?: number;
  };
  timestamp: string;
}

export interface WebSocketDetail {
  remoteAddress: string;
  port?: number;
  readyState: number;
  readyStateText?: string;
  connectedAt: string;
  connectionDuration?: string;
  path?: string;
  protocol?: string;
  bytesReceived?: string;
  bytesSent?: string;
  lastActivity?: string;
  userAgent?: string;
  userId?: string;
  deviceInfo?: string;
}

export interface SystemLog {
  timestamp: string;
  level: 'info' | 'warning' | 'warn' | 'error' | 'critical';
  message: string;
  source: string;
  details?: any;
}

export interface SystemLogs {
  logs: SystemLog[];
  timestamp: string;
}

export interface HistoricalLogs {
  date: string;
  logs: SystemLog[];
  total: number;
  limited: boolean;
  timestamp: string;
  filters: {
    level: string;
    source: string;
    errorsOnly: boolean;
  };
  availableDates: string[];
}

const API_URL = import.meta.env.VITE_API_URL;

export const systemService = {
  /**
   * Sistem diagnostik verilerini alır
   */
  getDiagnostics: async (): Promise<ServerDiagnostics> => {
    try {
      const response = await axios.get<ServerDiagnostics>(`${API_URL}/v1/system/diagnostics`);
      return response.data;
    } catch (error) {
      console.error('Sistem diagnostik verileri alınamadı:', error);
      throw error;
    }
  },
  
  /**
   * Canlı sistem log kayıtlarını alır (belleğin içindekiler)
   */
  getSystemLogs: async (): Promise<SystemLogs> => {
    try {
      const response = await axios.get<SystemLogs>(`${API_URL}/v1/system/logs`);
      return response.data;
    } catch (error) {
      console.error('Sistem log kayıtları alınamadı:', error);
      throw error;
    }
  },
  
  /**
   * Dosyadan geçmiş log kayıtlarını alır
   * @param date İstenen tarih (YYYY-MM-DD formatında)
   * @param filters Filtre seçenekleri
   */
  getHistoricalLogs: async (
    date?: string, 
    filters?: {
      level?: string;
      source?: string;
      errorsOnly?: boolean;
      limit?: number;
    }
  ): Promise<HistoricalLogs> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (filters?.level) params.append('level', filters.level);
      if (filters?.source) params.append('source', filters.source);
      if (filters?.errorsOnly) params.append('errorsOnly', 'true');
      if (filters?.limit) params.append('limit', filters.limit.toString());
      
      // Debug API çağrısı parametreleri
      console.log('Historical Log API çağrısı:', {
        endpoint: `${API_URL}/v1/system/logs/history`,
        filters: {
          date,
          level: filters?.level,
          source: filters?.source,
          errorsOnly: filters?.errorsOnly
        },
        queryString: params.toString()
      });

      const response = await axios.get<HistoricalLogs>(
        `${API_URL}/v1/system/logs/history`, 
        { params }
      );
      
      // Debug yanıtı
      console.log('API yanıtı:', {
        filtreler: response.data.filters,
        kayıt_sayısı: response.data.logs.length,
        sınırlandı: response.data.limited
      });
      
      return response.data;
    } catch (error) {
      console.error('Geçmiş log kayıtları alınamadı:', error);
      throw error;
    }
  }
};

export default systemService;
